import { yupResolver } from '@hookform/resolvers/yup';
import AddIcon from '@mui/icons-material/Add';
import { Box, Grid, IconButton, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Dispatch, SetStateAction, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { IConsultant } from 'wisestep-api';
import {
  ChipsType,
  ControlledTextInput,
  Label,
  WsButton,
  WsButtonGroup,
  WsColors,
  WsInputNumber,
  WsLocationSearch,
  WsSelect,
  WsSelectObj,
  WsTagsList,
} from 'wisestep-base';
import { string, object } from 'yup';

import { ActivateFeature } from '../../../../../components/ActivateFeature';
import { useTokenContext } from '../../../../../context'; //'@/context';
import { createCurrencyObject } from '../../../helper/CurrencyHelper';
import { visaOptions } from '../../../helper/VisaOptions';

interface IPrimaryInfoProps {
  newConsultant: (data: any) => void;
  setCurrentTab: Dispatch<SetStateAction<number>>;
  consultantData?: Partial<IConsultant>;
}

const BasicHeading = styled(Label)(() => ({
  color: WsColors.TheBlues600,
  fontWeight: 'bold',
  margin: '10px 0px',
}));

const ProfessionalHeading = styled(Label)(() => ({
  color: WsColors.TheBlues600,
  fontWeight: 'bold',
  margin: '10px 0px',
}));
const AddIconButton = ({ onClick }) => (
  <IconButton onClick={onClick}>
    <AddIcon sx={{ fill: WsColors.TheBlues500, cursor: 'pointer' }} fontSize={'medium'} />
  </IconButton>
);

const Input = styled('input')({
  display: 'none',
});

const PrimaryInfo = ({ newConsultant, setCurrentTab, consultantData }: IPrimaryInfoProps) => {
  const token = useTokenContext();
  const options: WsSelectObj[] = [
    {
      label: 'Public (To All Workspace Users)',
      value: 'Public',
    },
    {
      label: 'Private (Only You)',
      value: 'Private',
    },
    {
      label: 'Custom (Selected Teams/Users)',
      value: 'Shared',
    },
  ];
  const [skillIndex, setSkillIndex] = useState(0);
  const [skillsArray, setSkillsArray] = useState<string[]>(
    consultantData?.skills?.map((skill) => `${skill.skill}: ${skill.experienceInYears} years`) ?? []
  );
  const experienceOptions: WsSelectObj[] = [
    {
      label: 'Exp in Years',
      value: 'null',
    },
    {
      label: '0',
      value: 0,
    },
    {
      label: '1',
      value: 1,
    },
    {
      label: '2',
      value: 2,
    },
    {
      label: '3',
      value: 3,
    },
    {
      label: '4',
      value: 4,
    },
    {
      label: '5',
      value: 5,
    },
    {
      label: '6',
      value: 6,
    },
    {
      label: '7',
      value: 7,
    },
    {
      label: '8',
      value: 8,
    },
    {
      label: '9',
      value: 9,
    },
    {
      label: '10',
      value: 10,
    },
  ];

  const currencyOptions: WsSelectObj[] = createCurrencyObject();

  const schema = object().shape({
    name: string().required('Name  is required'),
    email: string().required('Email  is required'),
    location: object().required('City is required'),
    internalVisibility: string().required('Access Type is required'),
    visibility: string().required('Show consultants to active users is required'),
    jobTitle: string().required('Job Title is required'),
    visaStatus: string().required('Visa Status is required'),
  });

  const methods = useForm({
    resolver: yupResolver(schema),
    ...(consultantData && {
      defaultValues: {
        name: consultantData.name,
        email: consultantData.email,
        phoneNumber: consultantData.phoneNumber,
        location: consultantData?.location ?? [],
        preferredLocations: consultantData?.preferredLocations ?? [],
        internalVisibility: consultantData.internalVisibility,
        visibility: consultantData.visibility,
        jobTitle: consultantData.jobTitle,
        totalYearsExperience: consultantData.totalMonthsExperience,
        education: consultantData.education,
        visaStatus: consultantData.visaStatus,
        jobHarvestingQuery: consultantData.jobHarvestingQuery,
        salaryCurrency: consultantData.salaryCurrency,
        salaryPerHour: consultantData.salaryPerHour,
        skills: consultantData.skills ?? [],
      },
    }),
  });
  const visibility = (index: number, value: string) =>
    methods.setValue('visibility', value.toLocaleLowerCase() == 'no' ? 'MyWorkspace' : 'Public');

  const onSubmit = (data) => {
    newConsultant(data);
    setCurrentTab(1);
  };

  return (
    <FormProvider {...methods}>
      <Box>
        <BasicHeading>Basic Info</BasicHeading>
        <Grid container spacing={3} columnSpacing={3} component="form">
          <Grid item xs={6}>
            <ControlledTextInput
              name="name"
              labelText="Name *"
              type="text"
              fullWidth
              placeholder="Consultant Name"
            />
          </Grid>
          <Grid item xs={6}>
            <ControlledTextInput
              name="email"
              labelText="Email *"
              type="text"
              fullWidth
              placeholder="Consultant Email"
            />
          </Grid>
          <Grid item xs={6}>
            <ControlledTextInput
              name="phoneNumber"
              labelText="Phone Number"
              type="text"
              fullWidth
              placeholder="Consultant Phone"
            />
          </Grid>
          <Grid item xs={6}>
            <WsLocationSearch
              name={'location'}
              placeHolder={'Consultant City'}
              multiSelect={false}
              showMandatorySymbol
              wisestepApi={true}
              token={token.token}
              authorization={token.auth}
            />
          </Grid>
          <Grid item xs={6}>
            <ControlledTextInput
              name="consultNotes"
              labelText="General Note"
              type="text"
              fullWidth
              placeholder="Create notes from this consultant"
            />
          </Grid>
          <Grid item xs={6}>
            <WsLocationSearch
              name="preferredLocations"
              placeHolder="Preferred Location"
              wisestepApi={true}
              token={token.token}
              authorization={token.auth}
            />
          </Grid>
          <Grid item xs={6}>
            <WsSelect
              name="internalVisibility"
              label="Access consultant to Active Recruiters*"
              selectArray={options}
            />
          </Grid>
          <Grid item xs={6} display="flex" direction="column">
            <ActivateFeature feature="CL_PUBLIC_LISTING">
              <WsButtonGroup
                inputTitle="Show consultant to Active Recruiters*"
                buttonsList={['Yes', 'No']}
                selectedIndexValue={visibility}
              />
            </ActivateFeature>
          </Grid>
          <Grid item xs={6}>
            <Label>Resume</Label>
            <label htmlFor="contained-button-file">
              <Input
                name="resume"
                accept="image/*"
                id="contained-button-file"
                multiple
                type="file"
              />
              <WsButton variant="outlined">Upload Resume</WsButton>
            </label>
          </Grid>
          <Grid item xs={6}>
            <Label>Other Documents</Label>
            <label htmlFor="contained-button-file">
              <Input
                name="resume"
                accept="image/*"
                id="contained-button-file"
                multiple
                type="file"
              />
              <WsButton variant="outlined">Upload File</WsButton>
            </label>
          </Grid>
        </Grid>
      </Box>

      <Box pt={4}>
        <ProfessionalHeading>Professional Info</ProfessionalHeading>
        <Grid container spacing={3} columnSpacing={3} component="form">
          <Grid item xs={6}>
            <ControlledTextInput
              name="jobTitle"
              labelText="Job Title*"
              type="text"
              fullWidth
              placeholder="Job Title"
              required
            />
          </Grid>
          <Grid item xs={6}>
            <WsSelect
              name="totalYearsExperience"
              label="Experience In Years"
              selectArray={experienceOptions}
            />
          </Grid>
          <Grid item xs={6}>
            <ControlledTextInput
              name="education"
              labelText="Education "
              type="text"
              fullWidth
              placeholder="Education"
            />
          </Grid>
          <Grid item xs={6}>
            <WsSelect name="visaStatus" label="Visa Status*" selectArray={visaOptions} required />
          </Grid>
          <Grid item xs={6} display="flex" columnGap={3}>
            <WsSelect name="salaryCurrency" label="Currency" selectArray={currencyOptions} />
            <WsInputNumber name="salaryPerHour" label="Rate" />
          </Grid>
          <Grid item xs={6}>
            <ControlledTextInput
              name="jobHarvestingQuery"
              labelText="Job Search Criteria "
              type="text"
              fullWidth
              placeholder="Enter Search Criteria"
            />
          </Grid>
          <Grid item xs={6} display="flex" columnGap={3}>
            <ControlledTextInput
              name={`skills[${skillIndex}].skill`}
              labelText="Skills with Experience "
              type="text"
              fullWidth
              placeholder="Skill"
            />
            <WsSelect
              name={`skills[${skillIndex}].experienceInYears`}
              label=""
              selectArray={experienceOptions}
            />
            <AddIconButton
              onClick={() => {
                const newSkill: { skill?: string; experience?: number } =
                  methods.getValues('skills')[skillIndex];
                skillsArray.push(`${newSkill.skill}: ${newSkill.experience} years`);
                setSkillsArray(skillsArray);
                setSkillIndex(skillIndex + 1);
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <WsTagsList chipArray={skillsArray} count={3} type={ChipsType.PRIMARY} />
          </Grid>
          <Stack width="100%" direction="row-reverse" mt={1}>
            <WsButton onClick={methods.handleSubmit(onSubmit, (e) => console.log(e))}>
              Next
            </WsButton>
          </Stack>
        </Grid>
      </Box>
    </FormProvider>
  );
};
export default PrimaryInfo;
